import AirDatepicker from "air-datepicker";
import { Globalize } from "./_globalize";
import { createElement, uniqueId } from "./_dom";
import { registerComponent } from "./component";
import { initPopupContainer, closePopup } from "./_menu";

class DatePicker {
	constructor(field, options = {}) {
		this.#field = field;
		this.#trigger = null;
		this.#options = options;
		this.#init();
	}

	#onSelectDate = ({ /*date, */formattedDate/*, _datepicker*/ }) => {
		closePopup(DatePicker.#calendarPopup);
		if (this.#field.value !== formattedDate) {
			this.#field.value = formattedDate;
			this.#field.dispatchEvent(new Event("change", { bubbles: true }));
		}
		this.#field.focus();
	};

	#tryOpenCalendarPopup = () => {
		if (!this.#trigger.hasAttribute("aria-controls")) {
			DatePicker.#ensureCalendarPopup();
			this.#onCloseCalendarPopup();
			this.#trigger.setAttribute("aria-controls", DatePicker.#calendarPopup.id);
			const date = Globalize.parseDate(this.#field.value);
			DatePicker.#datePicker.update({
				...this.#options,
				startDate: date || new Date(),
				selectedDates: date ? [date] : [],
				onSelect: this.#onSelectDate
			}, { silent: true });
			DatePicker.#currentTrigger = this.#trigger;
		}
		this.#field.focus();
	};

	#onCloseCalendarPopup = () => {
		if (DatePicker.#currentTrigger) {
			DatePicker.#currentTrigger.removeAttribute("aria-controls");
			DatePicker.#currentTrigger.removeAttribute("aria-expanded");
			DatePicker.#currentTrigger = null;
		}
		DatePicker.#calendarPopup.popperInstance?.destroy();
		delete DatePicker.#calendarPopup.popperInstance;
	};

	#init() {
		const parent = this.#field.parentElement;
		const hasTimeField = parent.querySelector('.v3-input-time') != null;
		this.#field.classList.add("v3-controlgroup__item-first", "v3-datepicker__input");
		this.#trigger = createElement('button', {
				id: uniqueId("v3-datepicker-trigger"),
				type: "button",
				class: "v3-datepicker__trigger v3-controlgroup__item-last v3-button ui-icon-only js-auto-popup v3-popup-trigger",
				"aria-haspopup": "true",
				"data-popper-placement": "bottom-end",
				on: {
					click: this.#tryOpenCalendarPopup,
					"v3-popup.close": this.#onCloseCalendarPopup
				}
			},
			createElement("span", { class: "ui-icontext" }, "Toggle calendar")
		);
		const wrapper = createElement('nobr', {
			class: "v3-datepicker v3-controlgroup focusable"
		});
		this.#field.after(wrapper);
		wrapper.append(this.#field, this.#trigger);
		parent.classList.add('v3-has-date');
		if (hasTimeField) {
			parent.classList.add('v3-has-datetime');
		}
	}

	static #ensureCalendarPopup() {
		if (!DatePicker.#calendarPopup) {
			const calendar = Globalize.culture().calendar;
			const calendarPopupId = uniqueId("v3-datepicker-popup");
			const calendarPopup = createElement("div", { id: calendarPopupId, class: "v3-datepicker__popup v3-popup" });
			const container = initPopupContainer();
			container.append(calendarPopup);
			const datePicker = new AirDatepicker(calendarPopup, {
				toggleSelected: false,
				showOtherMonths: true,
				selectOtherMonths: true,
				inline: true,
				locale: {
					days: calendar.days.names,
					daysShort: calendar.days.namesAbbr,
					daysMin: calendar.days.namesShort,
					months: calendar.months.names.filter(e => e),
					monthsShort: calendar.months.namesAbbr.filter(e => e),
					firstDay: calendar.firstDay
				},
				dateFormat(date) {
					return Globalize.format(date, "d");
				},
			});
			DatePicker.#calendarPopup = calendarPopup;
			DatePicker.#datePicker = datePicker;
		}
	}

	static create(field, options) {
		return new DatePicker(field, options);
	}

	/** @type {HTMLInputElement}*/#field;
	/** @type {HTMLButtonElement}*/#trigger;
	/** @type {Object}*/#options;
	/** @type {HTMLDivElement}*/static #calendarPopup;
	/** @type {AirDatepicker}*/static #datePicker;
	/** @type {HTMLButtonElement}*/static #currentTrigger;
}

const datepicker = registerComponent(DatePicker.create);

function init(context, selector = "input.v3-input-date") {
	return datepicker(selector, context);
}

export { datepicker, init };